// Notebook physical elements: masking tape, sticky notes, torn paper, punch holes,
// ruled margin lines. Reused across the whole site so it feels like one notebook.

// A "roughen" filter + pencil defs. Render once near the root.
export const SketchDefs = () => (
  <svg width="0" height="0" className="absolute" aria-hidden="true">
    <defs>
      <filter id="pencil-rough">
        <feTurbulence type="fractalNoise" baseFrequency="0.014" numOctaves="2" seed="7" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="3.2" />
      </filter>
      <filter id="pencil-rough-strong">
        <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="3" seed="4" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="4.5" />
      </filter>
    </defs>
  </svg>
);

// Masking tape strip
export const Tape = ({ className = "", rotate = -4, width = 120, tint = "rgba(208,91,67,0.16)" }) => (
  <div
    className={`pointer-events-none select-none ${className}`}
    style={{ transform: `rotate(${rotate}deg)`, width }}
    aria-hidden="true"
  >
    <div
      className="h-8 border border-ink/10"
      style={{
        background: tint,
        backdropFilter: "saturate(1.1)",
        clipPath:
          "polygon(2% 8%, 98% 0, 100% 88%, 96% 100%, 4% 96%, 0 12%)",
        boxShadow: "inset 0 0 12px rgba(0,0,0,0.04)",
      }}
    >
      <div className="h-full w-full opacity-40" style={{ backgroundImage: "repeating-linear-gradient(90deg, transparent 0 6px, rgba(0,0,0,0.05) 6px 7px)" }} />
    </div>
  </div>
);

// Sticky note with folded corner + handwritten text
export const StickyNote = ({ children, className = "", rotate = 2, color = "#EAE5D9" }) => (
  <div
    className={`relative select-none ${className}`}
    style={{ transform: `rotate(${rotate}deg)` }}
    aria-hidden="true"
  >
    <div
      className="relative p-4 pr-6 shadow-[2px_4px_14px_rgba(18,20,24,0.12)]"
      style={{ background: color }}
    >
      <span className="font-hand text-ink text-lg md:text-xl leading-tight block">{children}</span>
      <span
        className="absolute bottom-0 right-0 w-5 h-5"
        style={{ background: "rgba(0,0,0,0.08)", clipPath: "polygon(100% 0, 0 100%, 100% 100%)" }}
      />
    </div>
  </div>
);

// Torn paper strip label
export const TornPaper = ({ children, className = "", rotate = -1.5 }) => (
  <div
    className={`inline-block bg-paper px-5 py-2 shadow-[1px_2px_8px_rgba(18,20,24,0.08)] ${className}`}
    style={{
      transform: `rotate(${rotate}deg)`,
      clipPath:
        "polygon(0 12%, 4% 0, 12% 10%, 22% 2%, 34% 12%, 46% 3%, 58% 12%, 70% 2%, 82% 12%, 92% 3%, 100% 12%, 100% 88%, 92% 98%, 80% 90%, 68% 99%, 54% 90%, 42% 99%, 30% 90%, 18% 99%, 8% 90%, 0 98%)",
    }}
  >
    <span className="font-hand text-ink text-lg">{children}</span>
  </div>
);

// Notebook binding punch holes (vertical strip)
export const PunchHoles = ({ className = "", count = 6 }) => (
  <div className={`flex flex-col justify-around items-center ${className}`} aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className="w-4 h-4 rounded-full bg-paper border border-ink/15 shadow-[inset_0_1px_3px_rgba(0,0,0,0.15)]" />
    ))}
  </div>
);

// A faint ruled-lines background (like notebook paper). Absolute-positioned.
export const RuledLines = ({ className = "" }) => (
  <div
    className={`pointer-events-none absolute inset-0 ${className}`}
    aria-hidden="true"
    style={{
      backgroundImage:
        "repeating-linear-gradient(to bottom, transparent 0 38px, rgba(18,20,24,0.05) 38px 39px)",
    }}
  />
);


// Small hand-labelled section title with a drawn underline (e.g. "STAGE MAP")
export const SectionLabel = ({ children, className = "", color = "text-ink", underline = "text-terracotta" }) => (
  <span className={`inline-block relative ${className}`}>
    <span className={`font-hand ${color}`} style={{ fontSize: "1.6rem", letterSpacing: "0.04em" }}>
      {children}
    </span>
    <svg viewBox="0 0 200 12" className={`absolute -bottom-2 left-0 w-full h-3 ${underline}`} preserveAspectRatio="none" aria-hidden="true">
      <path d="M4 7 Q60 2 100 6 T196 5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" filter="url(#pencil-rough)" />
    </svg>
  </span>
);

// "REHEARSAL #NN" notebook page header
export const RehearsalHeader = ({ n = "01", children, className = "" }) => (
  <div className={`flex items-baseline gap-3 ${className}`}>
    <span className="font-hand text-ink" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", letterSpacing: "0.06em" }}>
      REHEARSAL
    </span>
    <span className="font-hand text-terracotta" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}>#{n}</span>
    {children}
  </div>
);

// Hand-drawn boxed note (rough rectangle frame around content)
export const BoxedNote = ({ children, className = "", tone = "text-ink", rotate = 0 }) => (
  <div className={`relative inline-block ${className}`} style={{ transform: `rotate(${rotate}deg)` }}>
    <svg className={`absolute inset-0 w-full h-full ${tone}`} preserveAspectRatio="none" viewBox="0 0 100 100" aria-hidden="true">
      <path
        d="M3 4 L97 2 L98 97 L2 98 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.8"
        vectorEffect="non-scaling-stroke"
        filter="url(#pencil-rough)"
        style={{ strokeWidth: 1.6 }}
      />
    </svg>
    <div className="relative px-5 py-4">{children}</div>
  </div>
);

// Hand-drawn checklist
export const Checklist = ({ items = [], className = "" }) => (
  <ul className={`space-y-3 ${className}`}>
    {items.map((it, i) => (
      <li key={i} className="flex items-center gap-3">
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-ink shrink-0" aria-hidden="true">
          <path d="M4 4 L20 3 L21 20 L3 21 Z" fill="none" stroke="currentColor" strokeWidth="1.6" filter="url(#pencil-rough)" />
        </svg>
        <span className="font-hand text-ink" style={{ fontSize: "1.35rem", letterSpacing: "0.03em" }}>{it}</span>
      </li>
    ))}
  </ul>
);
